import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import { Dropdown } from 'primereact/dropdown'
import { Checkbox } from 'primereact/checkbox'
import React, { useCallback, useEffect, useState } from 'react'
import { HorseRegisterDto, Horse, HorseRegisterApi } from '../../../api'
import { useLocalState } from '../../../shared/UseLocalState'
import { appState } from '../../../state/App.state'
import { authState } from '../../../state/Auth.state'
import { horsesState } from '../../../state/Horses.state'
import { Button } from 'primereact/button'
import { Loading } from '../../loading/Loading'

interface LookupItem {
  id: number
  name: string
}

const NULL_DATE = new Date('1800-01-01')
const EMPTY_HORSE_REGISTER: HorseRegisterDto = {
  name: '',
  breedId: undefined,
  colorId: undefined,
  gender: '',
  father: '',
  fatherBreedId: undefined,
  mother: '',
  motherBreedId: undefined,
  birthday: NULL_DATE,
  owner: '',
  remark: '',
  size: undefined,
  isBigHorse: false,
  lNr: ''
}

//if (!bookerId) throw new Error(`booker id is mandatory`)

export const Horses = () => {
  /* STATE */
  const bookerId = authState.user$.value?.registerId
  const bookerEmail = authState.user$.value?.email
  const horses = useLocalState<Horse[]>(horsesState.horses$, horsesState.horses$.value)

  const [loading, setLoading] = useState<boolean>(false)
  const [form, setForm] = useState<HorseRegisterDto>(EMPTY_HORSE_REGISTER)
  const [breeds, setBreeds] = useState<LookupItem[]>([])
  const [colors, setColors] = useState<LookupItem[]>([])

  /* EFFECTS */
  useEffect(() => {
    const cfg = authState.apicfg()
    const headers: Record<string, string> = {}
    if (cfg.apiKey) {
      Promise.resolve(cfg.apiKey('X-API-KEY')).then((key) => {
        headers['X-API-KEY'] = key
        fetch(`${cfg.basePath}/api/Horse/breeds`, { headers })
          .then((r) => r.json())
          .then(setBreeds)
          .catch(() => {})
        fetch(`${cfg.basePath}/api/Horse/colors`, { headers })
          .then((r) => r.json())
          .then(setColors)
          .catch(() => {})
      })
    }
  }, [])

  /* DERIVED STATE */
  const formValid =
    !!form.name && form.name.trim().length > 0 &&
    !!form.breedId &&
    !!form.colorId &&
    !!form.gender &&
    !!form.owner && form.owner.trim().length > 0 &&
    !!form.mother && form.mother.trim().length > 0 &&
    !!form.motherBreedId &&
    !!form.father && form.father.trim().length > 0 &&
    !!form.fatherBreedId &&
    !!form.size &&
    !!form.birthday && form.birthday > NULL_DATE &&
    !!form.lNr && form.lNr.trim().length > 0

  /* LOGIC */
  const register = useCallback(() => {
    setLoading(true)
    if (bookerId == null) {
      setLoading(false)
    }
    form.bookerId = bookerId
    form.bookerEmail = bookerEmail

    new HorseRegisterApi(authState.apicfg())
      .apiHorseRegisterPostRaw({
        horseRegisterDto: form
      })
      .then(() => {
        appState.addToast('Registrierung erfolgreich!', 'success')
        //setForm(EMPTY_HORSE_REGISTER)
        //setForm(true)
      })
      .catch((err) => {
        appState.addToast('Registrierung fehlgeschlagen! Pr�fen Sie bitte die Felder', 'error')
        console.error(err)
      })
      .finally(() => setLoading(false))
  }, [form])

  /* TEMPLATE */
  return (
    <>
      <Loading loading={loading} />
      <div className='card' style={{ maxWidth: '600px', margin: '1rem auto', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h2 style={{ marginBottom: '0.25rem' }}>Neues Pferd registrieren</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            register()
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          {/* Name, Geschlecht & Rasse */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <InputText
              style={{ flex: 1 }}
              placeholder='Name *'
              value={form.name ?? ''}
              onChange={(e) => setForm((v) => ({ ...v, name: e.target.value ?? '' }))}
            />
            <Dropdown
              style={{ flex: 1 }}
              placeholder='Geschlecht'
              value={form.gender ?? null}
              options={[
                { label: 'Stute', value: 'S' },
                { label: 'Hengst', value: 'H' },
                { label: 'Wallach', value: 'W' }
              ]}
              optionLabel='label'
              optionValue='value'
              onChange={(e) => setForm((v) => ({ ...v, gender: e.value ?? '' }))}
            />
            <Dropdown
              style={{ flex: 1 }}
              placeholder='Rasse'
              value={form.breedId ?? null}
              options={breeds}
              optionLabel='name'
              optionValue='id'
              filter
              showClear
              onChange={(e) => setForm((v) => ({ ...v, breedId: e.value ?? undefined }))}
            />
          </div>
          {/* Farbe & Besitzer */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <Dropdown
              style={{ flex: 1 }}
              placeholder='Farbe'
              value={form.colorId ?? null}
              options={colors}
              optionLabel='name'
              optionValue='id'
              filter
              showClear
              onChange={(e) => setForm((v) => ({ ...v, colorId: e.value ?? undefined }))}
            />
            <InputText
              style={{ flex: 1 }}
              placeholder='Besitzer'
              value={form.owner ?? ''}
              onChange={(e) => setForm((v) => ({ ...v, owner: e.target.value ?? '' }))}
            />
          </div>
          {/* Mutter & Mutter-Rasse */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <InputText
              style={{ flex: 1 }}
              placeholder='Mutter'
              value={form.mother ?? ''}
              onChange={(e) => setForm((v) => ({ ...v, mother: e.target.value ?? '' }))}
            />
            <Dropdown
              style={{ flex: 1 }}
              placeholder='Rasse Mutter'
              value={form.motherBreedId ?? null}
              options={breeds}
              optionLabel='name'
              optionValue='id'
              filter
              showClear
              onChange={(e) => setForm((v) => ({ ...v, motherBreedId: e.value ?? undefined }))}
            />
          </div>
          {/* Vater & Vater-Rasse */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <InputText
              style={{ flex: 1 }}
              placeholder='Vater'
              value={form.father ?? ''}
              onChange={(e) => setForm((v) => ({ ...v, father: e.target.value ?? '' }))}
            />
            <Dropdown
              style={{ flex: 1 }}
              placeholder='Rasse Vater'
              value={form.fatherBreedId ?? null}
              options={breeds}
              optionLabel='name'
              optionValue='id'
              filter
              showClear
              onChange={(e) => setForm((v) => ({ ...v, fatherBreedId: e.value ?? undefined }))}
            />
          </div>
          {/* Stockmaß & Großpferd */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
            <InputNumber
              style={{ flex: 1 }}
              placeholder='Stockmaß (cm)'
              value={form.size ?? null}
              onValueChange={(e) => {
                const val = e.value ?? undefined
                setForm((v) => ({
                  ...v,
                  size: val,
                  isBigHorse: val != null && val >= 144 ? v.isBigHorse : false
                }))
              }}
              min={0}
              max={300}
              maxFractionDigits={0}
              suffix=' cm'
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
              <Checkbox
                inputId='isBigHorse'
                checked={!!form.isBigHorse}
                disabled={!form.size || form.size < 144}
                onChange={(e) => setForm((v) => ({ ...v, isBigHorse: e.checked ?? false }))}
              />
              <label htmlFor='isBigHorse'>Großpferd</label>
            </div>
          </div>
          {/* Geburtstag */}
          <Calendar
            style={{ width: '100%' }}
            placeholder='Geburtstag'
            value={(form?.birthday ?? new Date(0)) > new Date(0) ? form.birthday : null}
            dateFormat='dd.mm.yy'
            onChange={(e) => setForm((v) => ({ ...v, birthday: e.value ?? new Date(0) }))}
            showIcon
          />
          {/* Lebensnummer */}
          <InputText
            style={{ width: '100%' }}
            placeholder='Lebensnummer'
            value={form.lNr ?? ''}
            onChange={(e) => setForm((v) => ({ ...v, lNr: e.target.value ?? '' }))}
          />
          {/* Anmerkung */}
          <InputText
            style={{ width: '100%' }}
            placeholder='Anmerkung'
            value={form.remark ?? ''}
            onChange={(e) => setForm((v) => ({ ...v, remark: e.target.value ?? '' }))}
          />
        </form>
        <Button onClick={register} disabled={!formValid}>
          Registrieren
        </Button>
        <Button outlined onClick={() => window.history.back()}>
          Zurück
        </Button>
      </div>
    </>
  )
}
